import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fade,
  Typography,
  Box,
  Select,
  MenuItem,
  Card,
} from "@mui/material";
import api from "../../api/axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { PermissionContext } from "../Context/Context";

const ChangeMode = ({
  open,
  setOpen = () => {},
  onClose,
  loading = false,
  currentRoleId,
}) => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const { setNewRoleConfig, newRoleConfig } = useContext(PermissionContext);

  // console.log(currentRoleId);

  const rolesData = async () => {
    try {
      const response = await api.post(process.env.REACT_APP_API_URL + "/roles");

      // console.log(response);

      const { user_types, status } = response.data;
      const type = roles?.map((role) => ({ type: role.type }));

      // Crear un Set para obtener valores únicos
      const user_levelSet = new Set(roles?.map((role) => role.user_level));
      const user_level = Array.from(user_levelSet).map((level) => ({
        user_level: level,
      }));

      // Ordenar user_level
      user_level.sort((a, b) => a.user_level - b.user_level);

      if (user_types?.total === 0) {
        return Swal.fire({
          icon: "error",
          title: "No se encontraron resultados",
          text: "Modifica tu búsqueda",
        });
      }

      setRoles(user_types?.data);
    } catch (error) {
      console.error("Error fetching roles:", error);

      const errorStatus = error?.response?.statusText == "Forbidden";

      const errorLog = errorStatus
        ? "No estás autorizado para entrar a esta sección."
        : "Error en el servidor.";

      Swal.fire({
        icon: "error",
        title: errorLog,
        text: "Hubo un problema al cargar los datos.",
        allowEscapeKey: !errorLog,
        allowOutsideClick: !errorLog,
      }).then((event) => {
        if (errorLog && event.isConfirmed) {
          navigate("/");
        }
      });
    }
  };

  const activateTestMode = async () => {
    if (!selectedRole) return;

    const confirm = await Swal.fire({
      title: "Activar modo de prueba",
      text: `El sistema usará temporalmente el rol "${selectedRole.type}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Activar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1ab394",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;

    Swal.fire({
      title: "Aplicando modo de prueba…",
      text: "Actualizando permisos en tiempo real",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    //  Simula un pequeño delay para UX (opcional pero recomendado)
    await new Promise((resolve) => setTimeout(resolve, 700));

    // Cambio real de permisos
    setNewRoleConfig(selectedRole);

    Swal.fire({
      icon: "success",
      title: "Modo de prueba activado",
      text: `Rol activo: ${selectedRole.type}`,
      timer: 1800,
      showConfirmButton: false,
    });

    setOpen(false);
  };

  const restoreOriginalRole = async () => {
    if (!newRoleConfig) return;

    const confirm = await Swal.fire({
      title: "Volver al rol original",
      text: `Se restaurará el rol "${currentRoleId?.rol_relation?.type}".`,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Restaurar",
      confirmButtonColor: "#1ab394",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;

    setNewRoleConfig(null); //  vuelve al estado real
    setSelectedRole(null); // limpia preview / select

    Swal.fire({
      icon: "success",
      title: "Rol restaurado",
      text: `Rol activo: ${currentRoleId?.rol_relation?.type}`,
      timer: 1600,
      showConfirmButton: false,
    });
    setOpen(false);
  };

  useEffect(() => {
    rolesData();
  }, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {loading ? "Verificando usuarios…" : "Cambiar modo de usuario"}
      </DialogTitle>

      <DialogContent>
        <Fade in>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Modo actual */}

            <Box
              sx={{
                p: "5px",
                borderRadius: 1,
                backgroundColor: "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}>
              <Typography
                fontWeight={600}
                variant="caption"
                mr={1}
                color="text.secondary">
                ROL ORIGINAL:
              </Typography>
              <Card
                variant="outlined"
                color="info"
                sx={{
                  p: "5px",
                  display: "flex",
                  alignItems: "center",
                }}>
                <Typography fontWeight={600}>
                  {currentRoleId?.rol_relation?.type ?? "—"}
                </Typography>
              </Card>
              <Button
                color="info"
                variant="outlined"
                size="small"
                disabled={!newRoleConfig}
                onClick={restoreOriginalRole}>
                Volver al rol original
              </Button>
            </Box>

            {newRoleConfig && (
              <Box
                sx={{
                  p: "5px",
                  borderRadius: 1,
                  backgroundColor: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                <Typography
                  fontWeight={600}
                  variant="caption"
                  mr={1}
                  color="text.secondary">
                  MODO DE PRUEBAS ACTIVADO, ROL:
                </Typography>
                <Card
                  variant="outlined"
                  color="info"
                  sx={{
                    p: "5px",
                    display: "flex",
                    alignItems: "center",
                  }}>
                  <Typography fontWeight={600}>
                    {newRoleConfig?.type ?? "—"}
                  </Typography>
                </Card>
              </Box>
            )}

            {/* Selector */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5 }}>
                Nuevo modo
              </Typography>

              <Select
                fullWidth
                size="small"
                value={selectedRole?.id ?? ""}
                displayEmpty
                onChange={(e) => {
                  const role = roles.find((r) => r.id === e.target.value);
                  setSelectedRole(role);
                }}>
                <MenuItem value="">
                  <em>Selecciona un rol</em>
                </MenuItem>

                {roles
                  .filter((r) => r.id !== currentRoleId?.rol_relation?.id)
                  .map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.type}
                    </MenuItem>
                  ))}
              </Select>
            </Box>

            {/* Preview */}
            {selectedRole && (
              <Fade in>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px dashed #1ab394",
                    display: "flex",
                    alignItems: "center",
                  }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    El usuario cambiará a:
                  </Typography>
                  <Card variant="outlined">
                    <Typography fontWeight={600} p={"5px"} color="success.main">
                      {selectedRole.type}
                    </Typography>
                  </Card>
                </Box>
              </Fade>
            )}
          </Box>
        </Fade>
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancelar
        </Button>

        <Button
          variant="contained"
          color="success"
          disabled={!selectedRole || loading}
          onClick={activateTestMode}>
          Confirmar cambio
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeMode;
